<script lang="ts">
    import {
        KEY_CODE_ARROW_LEFT,
        KEY_CODE_ARROW_RIGHT,
        KEY_CODE_ESC,
        KEY_CODE_SPACE,
    } from "@/constants";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import ArrowIconImageViewer from "@components/icons/ArrowIconImageViewer.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import FullScreenIcon from "@components/icons/FullScreenIcon.svelte";

    import ImageViewerController from "./ImageViewerController";

    export let imageViewerController: ImageViewerController;
    const currentImage$ = imageViewerController.currentImage$;
    const currentIndex$ = imageViewerController.currentIndex$;
    const imageLenght$ = imageViewerController.imagesLength$;
    const hasNext$ = imageViewerController.hasNext$;
    const hasPrev$ = imageViewerController.hasPrev$;
    const handleNext = () => {
        imageViewerController.next();
    };
    const handlePrev = () => {
        imageViewerController.prev();
    };
    const handleBackdropClick = () => {
        imageViewerController.closeImage();
    };
    const handleClose = () => {
        imageViewerController.closeImage();
    };
    const handleFullScreen = () => {
        imageViewerController.toggeleFullscreen();
    };

    const handleKeydown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case KEY_CODE_ARROW_RIGHT:
                handleNext();
                break;
            case KEY_CODE_ARROW_LEFT:
                handlePrev();
                break;
            case KEY_CODE_ESC:
                handleClose();
                break;
            case KEY_CODE_SPACE:
                handleFullScreen();
                break;
        }
    };

    $: {
        $currentImage$;
        if ($currentImage$) {
            document.body.classList.add("gw-modal-open");
            document.addEventListener("keydown", handleKeydown);
        } else {
            document.body.classList.remove("gw-modal-open");
            document.removeEventListener("keydown", handleKeydown);
        }
    }
</script>

<div
    class="gw-image-viewer"
    tabindex="-1"
    class:gw-image-viewer--open={$currentImage$}
>
    <div class="gw-image-viewer__backdrop" on:click={handleBackdropClick} />
    <div class="gw-image-viewer__header">
        <div>
            {#if $currentIndex$ !== null && $imageLenght$}
                {$currentIndex$ + 1} / {$imageLenght$}
            {/if}
        </div>
        <div class="gw-image-viewer__header-controlls">
            <BaseButton
                class="gw-image-viewer__header-btn"
                on:click={handleFullScreen}
            >
                <Icon>
                    <FullScreenIcon />
                </Icon>
            </BaseButton>
            <BaseButton
                on:click={handleClose}
                class="gw-image-viewer__header-btn"
            >
                <Icon>
                    <CloseIcon />
                </Icon>
            </BaseButton>
        </div>
    </div>
    <div class="gw-image-viewer__main">
        {#if $currentImage$}
            <img src={$currentImage$} alt="" class="gw-image-viewer__img" />

            <div class="gw-image-viewer__arrows">
                {#if $hasPrev$}
                    <BaseButton
                        class="gw-image-viewer__arrow gw-image-viewer__arrow--prev"
                        on:click={handlePrev}
                    >
                        <ArrowIconImageViewer />
                    </BaseButton>
                {/if}
                {#if $hasNext$}
                    <BaseButton
                        class="gw-image-viewer__arrow gw-image-viewer__arrow--next"
                        on:click={handleNext}
                    >
                        <ArrowIconImageViewer />
                    </BaseButton>
                {/if}
            </div>
        {/if}
    </div>
</div>

<style lang="postcss">
    .gw-image-viewer {
        display: none;
        position: fixed;
        z-index: var(--gw-z-index-modal);
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .gw-image-viewer--open {
        display: block;
    }

    .gw-image-viewer__backdrop {
        display: block;
        position: fixed;
        right: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: #000;
    }

    .gw-image-viewer__header {
        display: flex;
        position: absolute;
        justify-content: space-between;
        width: 100%;
        padding: 10px 20px;
        color: #fff;
        box-sizing: border-box;
    }

    .gw-image-viewer__main {
        width: 100%;
        height: 100%;
        padding: 60px;
        box-sizing: border-box;
        text-align: center;
    }

    .gw-image-viewer__img {
        object-fit: contain;
        position: relative;
        width: 100%;
        height: 100%;
    }

    .gw-image-viewer__header-controlls {
        margin-right: -20px;
    }

    :global(.gw-btn.gw-image-viewer__header-btn) {
        margin-right: 20px;
        color: #fff;
    }

    :global(.gw-image-viewer__arrow) {
        position: absolute;
        top: 50%;
        width: 40px;
        height: 40px;
        padding: 6px;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover,
        &:focus {
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.2);
        }
    }

    :global(.gw-image-viewer__arrow--prev) {
        left: 10px;
        transform: translateY(-50%) rotateZ(180deg);
    }

    :global(.gw-image-viewer__arrow--next) {
        right: 10px;
    }
</style>
